import mapboxgl from 'mapbox-gl';
import {
  MAPBOX_STYLE,
  MELBOURNE_COORDINATES,
  MARKER_COLORS,
  POINTS_SOURCE,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  AUTO_PATH_SOURCE,
  AUTO_PATH_LAYER,
  CUSTOM_PATH_SOURCE,
  CUSTOM_PATH_LAYER,
  DISTANCE_LABELS_SOURCE,
  DISTANCE_LABELS_LAYER,
  PATH_STYLES
} from './constants';
import { LocationType } from '../Sidebar/LocationList';
import { setupMarkerHoverEvents } from './MapMarkers';

// Initialize map with all required map layers
export const initializeMap = ({
  mapContainer,
  setMapError,
  setMapReady,
  sourcesReadyRef,
  shouldShowDistanceLabels
}) => {
  if (!mapContainer?.current) {

    return null;
  }
  
  try {
    console.log('[TSP-MAP] Starting map initialization...');
    
    // Initialize map
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('mapbox_token') || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    
    console.log('[TSP-MAP] Token check:', {
      hasUrlToken: !!urlParams.get('mapbox_token'),
      hasEnvToken: !!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
      hasToken: !!token
    });
    
    if (!token) {
      console.error('[TSP-MAP] Missing Mapbox access token');
      setMapError('Missing Mapbox access token. Please add ?mapbox_token=your_token to URL or set REACT_APP_MAPBOX_ACCESS_TOKEN in .env');
      return null;
    }
    
    mapboxgl.accessToken = token;
    
    // Create map instance
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAPBOX_STYLE,
      center: [MELBOURNE_COORDINATES.lng, MELBOURNE_COORDINATES.lat],
      zoom: 10,
      attributionControl: false
    });
    
    // Expose a global reloadMap function for debugging
    window.reloadMap = (pathData) => {
      console.log('[MAP-RELOAD] Reload map requested', {
        hasCustomPathData: !!pathData,
        dataType: pathData ? typeof pathData : 'none'
      });
      
      if (!map) {

        return;
      }
      
      if (!sourcesReadyRef.current) {

        return;
      }
      
      try {
        if (pathData && map.getSource(AUTO_PATH_SOURCE)) {

          map.getSource(AUTO_PATH_SOURCE).setData(pathData);
          
          if (map.getLayer(AUTO_PATH_LAYER)) {
            map.setLayoutProperty(AUTO_PATH_LAYER, 'visibility', 'visible');
          }
          
          map.triggerRepaint();

          return; 
        }
      } catch (err) {

      }
      
      try {

        if (map.getLayer(AUTO_PATH_LAYER)) {

          map.removeLayer(AUTO_PATH_LAYER);
        }
        
        if (map.getLayer(CUSTOM_PATH_LAYER)) {

          map.removeLayer(CUSTOM_PATH_LAYER);
        }
        
        if (map.getSource(AUTO_PATH_SOURCE)) {

          map.removeSource(AUTO_PATH_SOURCE);
        }
        
        if (map.getSource(CUSTOM_PATH_SOURCE)) {

          map.removeSource(CUSTOM_PATH_SOURCE);
        }

        map.addSource(AUTO_PATH_SOURCE, {
          type: 'geojson',
          data: pathData || {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: [] }
          }
        });

        map.addSource(CUSTOM_PATH_SOURCE, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: [] }
          }
        });

        map.addLayer({
          id: AUTO_PATH_LAYER,
          type: 'line',
          source: AUTO_PATH_SOURCE,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': 'visible'
          },
          paint: {
            'line-color': PATH_STYLES.auto.color,
            'line-width': PATH_STYLES.auto.width,
            'line-opacity': PATH_STYLES.auto.opacity
          }
        }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer

        map.addLayer({
          id: CUSTOM_PATH_LAYER,
          type: 'line',
          source: CUSTOM_PATH_SOURCE,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': 'visible'
          },
          paint: {
            'line-color': PATH_STYLES.custom.color,
            'line-width': PATH_STYLES.custom.width,
            'line-opacity': PATH_STYLES.custom.opacity,
            'line-dasharray': [2, 1] // Dashed line for custom path
          }
        }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer
        
        map.triggerRepaint();

      } catch (err) {

        // Try one final fallback method - create unique IDs
        try {

          const timestamp = Date.now();
          const autoLayerId = `${AUTO_PATH_LAYER}-${timestamp}`;
          const autoSourceId = `${AUTO_PATH_SOURCE}-${timestamp}`;
          
          map.addSource(autoSourceId, {
            type: 'geojson',
            data: pathData || {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: [] }
            }
          });
          
          map.addLayer({
            id: autoLayerId,
            type: 'line',
            source: autoSourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
              'visibility': 'visible'
            },
            paint: {
              'line-color': PATH_STYLES.auto.color,
              'line-width': PATH_STYLES.auto.width,
              'line-opacity': PATH_STYLES.auto.opacity
            }
          }, POINTS_CIRCLE_LAYER);

        } catch (fallbackErr) {

        }
      }
    };
    
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    
    // Use a safely referenced variable to track map instance
    let mapRef = map;
    
    // Listen for container size changes to resize map
    const resizeObserver = new ResizeObserver(() => {
      if (mapRef) {
        try {
          // Check if map container exists and is connected to DOM
          if (mapContainer.current && document.body.contains(mapContainer.current)) {
            mapRef.resize();
          }
        } catch (error) {

        }
      }
    });
    
    if (mapContainer.current) {
      resizeObserver.observe(mapContainer.current);
    }
    
    // When map loads, initialize data sources and layers
    map.on('load', () => {
      try {
        console.log('[TSP-MAP] Map loaded, initializing layers...');
        initializeMapLayers(map, shouldShowDistanceLabels);
        setupMarkerHoverEvents(map);
        
        sourcesReadyRef.current = true;
        setMapReady(true);
        console.log('[TSP-MAP] Map initialization completed successfully');
      } catch (err) {
        console.error('[TSP-MAP] Error initializing map layers:', err);
        setMapError("Error initializing map data sources and layers: " + err.message);
      }
    });
    
    // Cleanup function to ensure map reference is cleared
    const cleanup = () => {
      mapRef = null;
    };
    
    return { map, resizeObserver, cleanup };
  } catch (error) {
    console.error('[TSP-MAP] Critical error during map initialization:', error);
    setMapError("Error initializing map: " + error.message);
    return null;
  }
};

// Initialize map layers
export const initializeMapLayers = (map, shouldShowDistanceLabels) => {
  if (!map) {
    console.warn('[TSP-MAP] initializeMapLayers called with null map');
    return;
  }
  
  console.log('[TSP-MAP] Starting layer initialization...', {
    shouldShowDistanceLabels,
    mapStyle: map.getStyle()?.name || 'Unknown'
  });
  
  // 1. Add point data source
  map.addSource(POINTS_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 2. Add point circle layer
  map.addLayer({
    id: POINTS_CIRCLE_LAYER,
    type: 'circle',
    source: POINTS_SOURCE,
    paint: {
      // Circle color - set based on point type or if it's a start point
      'circle-color': [
        'case',
        ['==', ['get', 'isStart'], true], MARKER_COLORS[LocationType.START],
        ['==', ['get', 'type'], LocationType.END], MARKER_COLORS[LocationType.END],
        ['==', ['get', 'type'], LocationType.WAYPOINT], MARKER_COLORS[LocationType.WAYPOINT],
        MARKER_COLORS[LocationType.NORMAL]
      ],
      // Responsive circle size - based on zoom level
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 8,    // Zoom level 8: radius 8px (city level)
        10, 12,  // Zoom level 10: radius 12px
        12, 14,  // Zoom level 12: radius 14px (default size)
        15, 18,  // Zoom level 15: radius 18px (street level)
        18, 24   // Zoom level 18: radius 24px (building level)
      ],
      // Responsive stroke width
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 1,    // City level: thin stroke
        12, 2,   // Default level: normal stroke
        15, 3,   // Street level: thick stroke
        18, 4    // Building level: thickest stroke
      ],
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.9
    }
  });
  
  // 3. Add point symbol layer - numbers displayed directly on points
  map.addLayer({
    id: POINTS_SYMBOL_LAYER,
    type: 'symbol',
    source: POINTS_SOURCE,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      // Responsive text size - based on zoom level
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 14,   // Zoom level 8: text 14px (city level)
        10, 18,  // Zoom level 10: text 18px
        12, 22,  // Zoom level 12: text 22px (default size)
        15, 26,  // Zoom level 15: text 26px (street level)
        18, 32   // Zoom level 18: text 32px (building level)
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'center', // Ensure text is centered on point
      'text-offset': [0, 0],   // Avoid any offset
      'text-justify': 'center' // Text internal alignment
    },
    paint: {
      'text-color': '#ffffff',
      // Responsive text halo width
      'text-halo-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0.3,   // City level: thin halo
        12, 0.5,  // Default level: normal halo
        15, 0.8,  // Street level: thick halo
        18, 1.2   // Building level: thickest halo
      ],
      'text-halo-color': 'rgba(0,0,0,0.3)'
    }
  });
  
  // 4. Add auto path line data source
  map.addSource(AUTO_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        pathType: 'auto'
      },
      geometry: {
        type: 'LineString',
        coordinates: []
      }
    }
  });
  
  // 5. Add auto path line layer
  map.addLayer({
    id: AUTO_PATH_LAYER,
    type: 'line',
    source: AUTO_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible' // Initially set to visible, controlled by separate useEffect
    },
    paint: {
      'line-color': PATH_STYLES.auto.color,
      // Responsive auto path line width
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 2,    // City level: thin lines
        10, 3,   // Medium level
        12, 4,   // Default level: normal lines
        15, 5,   // Street level: thick lines
        18, 7    // Building level: thickest lines
      ],
      'line-opacity': PATH_STYLES.auto.opacity,
      'line-dasharray': [1, 0] // Auto path uses solid line
    }
  }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer to ensure points are on top
  
  // 6. Add custom path line data source
  map.addSource(CUSTOM_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {
        pathType: 'custom'
      },
      geometry: {
        type: 'LineString',
        coordinates: []
      }
    }
  });
  
  // 7. Add custom path line layer
  map.addLayer({
    id: CUSTOM_PATH_LAYER,
    type: 'line',
    source: CUSTOM_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible' // Initially set to visible, controlled by separate useEffect
    },
    paint: {
      'line-color': PATH_STYLES.custom.color,
      // Responsive custom path line width
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 1.5,  // City level: thin lines
        10, 2.5, // Medium level
        12, 3,   // Default level: normal lines
        15, 4,   // Street level: thick lines
        18, 6    // Building level: thickest lines
      ],
      'line-opacity': PATH_STYLES.custom.opacity,
      // Static dash pattern (dasharray cannot be interpolated)
      'line-dasharray': [2, 2]
    }
  }, POINTS_CIRCLE_LAYER); // Add BEFORE the points layer to ensure points are on top
  
  // 8. Add distance label data source
  map.addSource(DISTANCE_LABELS_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 9. Add distance label layer
  map.addLayer({
    id: DISTANCE_LABELS_LAYER,
    type: 'symbol',
    source: DISTANCE_LABELS_SOURCE,
    layout: {
      'text-field': ['get', 'distance'],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      // Responsive distance label text size
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 10,   // Zoom level 8: text 10px (city level)
        10, 12,  // Zoom level 10: text 12px
        12, 14,  // Zoom level 12: text 14px (default size)
        15, 16,  // Zoom level 15: text 16px (street level)
        18, 20   // Zoom level 18: text 20px (building level)
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'symbol-placement': 'point',
      'text-justify': 'center',
      'text-anchor': 'center',
      'visibility': shouldShowDistanceLabels ? 'visible' : 'none'
    },
    paint: {
      'text-color': '#000000',
      // Responsive distance label halo width
      'text-halo-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 1.5,   // City level: thin halo
        12, 2,    // Default level: normal halo
        15, 2.5,  // Street level: thick halo
        18, 3     // Building level: thickest halo
      ],
      'text-halo-color': 'rgba(255,255,255,0.9)'
    }
  });
  
  console.log('[TSP-MAP] All map layers initialized successfully');
}; 